import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { PopupCloseMode } from "@features/control-points/models/popupMode";
import { SelectedEpsg } from "@features/vibration-events/models/select-epsg";
import { NgSelectComponent } from "@ng-select/ng-select";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { EpsgIndex } from "@shared/models/epsgData";
import { CurrentStateService } from "app/core/services/current-state.service";
import { debounceTime, distinctUntilChanged, of, Subject, switchMap, takeUntil, tap } from "rxjs";
import epsgIndexData from 'epsg-index/all.json';
import { Site } from "@shared/models/site";
import { Standard } from "@shared/models/standard";
import { SitesAreaService } from "../../services/sites-area.service";
import { FonctionsService } from "@shared/services/utilities/fonctions.service";
import { PonderationService } from "@shared/services/ponderation/ponderation.service";

@Component({
  selector: 'app-popup-site',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    NgSelectComponent
  ],
  templateUrl: './site-modal.component.html',
  styleUrl: './site-modal.component.scss'
})

export class SiteModaleComponent implements OnInit {
  @ViewChild('popup') popup!: ElementRef;
  @Output() close = new EventEmitter<PopupCloseMode>();
  private unsubscribe$ = new Subject<void>();
  protected isNew: boolean = false;
  siteId: number | null = null;
  site: Site | null = null;
  protected form: FormGroup;
  protected epsgCodes: any[] = [];
  protected selectedEpsg?: SelectedEpsg;
  protected epsgIndex: EpsgIndex = {};
  protected filteredEpsgCodes: any[] = [];
  protected filteredEpsgSiteCodes: any[] = [];
  protected typeahead$ = new Subject<string>();
  protected typeaheadSite$ = new Subject<string>();
  protected loadingEpsg = false;
  protected loadingEpsgSite = false;
  protected countries: any[] | undefined;
  protected regionId: string | undefined;
  protected regions: any[] | undefined;
  ponderations: any[] = [];
  selectedPonderationId: number | null = null;
  selectedStandardCode: string | null = null;
  constructor(
    protected currentStateService: CurrentStateService,
    private dialogRef: MatDialogRef<SiteModaleComponent>,
    private sitesAreaService: SitesAreaService,
    private fonctionsService: FonctionsService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef, private ponderationService: PonderationService
  ) {
    this.form = new FormGroup({
      name: new FormControl(null, [Validators.required]),
      region: new FormControl(null, [Validators.required]),
      country: new FormControl(null, [Validators.required]),
      epsgSite: new FormControl(null, [Validators.required]),
      epsg: new FormControl(null),
      x: new FormControl(null, [Validators.required]),
      y: new FormControl(null, [Validators.required]),
      isActif: new FormControl(true),
    });

    this.currentStateService.setClosePopup(null);

    this.currentStateService.closePopup$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (closeMode: PopupCloseMode | null) => {
        if (closeMode != null && closeMode === PopupCloseMode.CLOSE) {
          this.onClose(closeMode);
        }
      }
    });
  }

  ngOnInit(): void {
    this.getAllStandards();
    this.isNew = this.data?.isNew;
    this.siteId = this.data?.siteId;
    this.countries = this.data?.countries;
    this.regionId = this.data?.regionId;
    this.regions = this.data?.regions;
    this.epsgIndex = this.parseEpsgIndex(epsgIndexData);
    this.epsgCodes = Object.keys(this.epsgIndex).map(code => ({
      id: code,
      text: `${this.epsgIndex[code].name} (${code})`
    }));
    this.filteredEpsgCodes = [...this.epsgCodes];
    this.filteredEpsgSiteCodes = [...this.epsgCodes];
    this.typeahead$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.loadingEpsg = true)),
        switchMap(term => this.searchEpsg(term))
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: results => {
          this.filteredEpsgCodes = results;
          this.loadingEpsg = false;
        },
        error: () => {
          this.loadingEpsg = false;
        },
        complete: () => {
          this.loadingEpsg = false;
        }
      });

    this.typeaheadSite$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.loadingEpsgSite = true)),
        switchMap(term => this.searchEpsg(term))
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: results => {
          this.filteredEpsgSiteCodes = results;
          this.loadingEpsgSite = false;
        },
        error: () => {
          this.loadingEpsgSite = false;
        },
        complete: () => {
          this.loadingEpsgSite = false;
        }
      });
    if(!this.isNew && this.siteId) {
      this.sitesAreaService.countries$.subscribe(countries => {
        this.countries = countries;
      });
      this.sitesAreaService.regions$.subscribe(regions => {
        this.regions = regions;
      });
      this.sitesAreaService.getSiteById(this.siteId).subscribe(result => {
        this.site = result;
        if (this.site) {
          const selectedEpsgSite = this.filteredEpsgSiteCodes.find(
            e => e.id === this.site.epsg
          );
          const selectedEpsg = this.filteredEpsgCodes.find(
            e => e.id === this.site.epsgCoordinates
          );
          this.form.patchValue({
            name: this.site.nom,
            region: this.site.regionId,
            country: this.site.paysId,
            epsg: selectedEpsg,
            epsgSite: selectedEpsgSite,
            x: this.site.coordX,
            y: this.site.coordY,
            isActif: this.site.isActif,
          });
          this.getcurrentPonderation();
          this.cdr.detectChanges();
        }
      });
    }
    if(this.regionId) {
      this.form.patchValue({
        region: this.regionId
      });
    }
  }
  getcurrentPonderation() {
    this.ponderationService.getStandardCodeBySiteId(this.siteId).subscribe({
      next: (response) => {
        this.selectedStandardCode = response.data ?? null;
        console.log('Current standard code response:', response);
        console.log(this.ponderations);
        
        this.cdr.detectChanges();
      }
    });
  }
  getAllStandards() {
    this.ponderationService.getAllStandards().subscribe({
      next: (response) => {
        this.ponderations = (response.data ?? []).map((standard: Standard & { Id?: number; Code?: string; Country?: string }) => ({
          ponderationId: standard.id ?? standard.Id,
          standardCode: standard.code ?? standard.Code ?? null,
          name: `${standard.code ?? standard.Code}${(standard.country ?? standard.Country) ? ' - ' + (standard.country ?? standard.Country) : ''}`
        }));
      }
    });
  }

  searchEpsg(term: string) {
    if (!term || term.trim() === '') {
      return of([...this.epsgCodes]);
    }
    const lowerTerm = term.toLowerCase();
    const results = this.epsgCodes.filter(code => 
      code.text.toLowerCase().includes(lowerTerm) || code.id.toLowerCase().includes(lowerTerm)
    );
    return of(results);
  }

  protected onClose(closeMode: PopupCloseMode = PopupCloseMode.CLOSE, updatedSite: Site | null = null) {
    this.close.emit(closeMode);
    this.dialogRef.close(updatedSite);
  }

onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
        return;
    }

    const updatePonderation = (siteId: number) => {      
      const selectedStandardCode = this.getSelectedStandardCode();
      console.log('Updating ponderation with ID:', this.selectedPonderationId, 'and standard code:', selectedStandardCode);
      
      this.ponderationService.updateSitePonderation(siteId, null, selectedStandardCode).subscribe({
            next: () => {
                this.fonctionsService.launch_toast(true, this.translate.instant('SUCCESS.PONDERATION_UPDATED'));
            },
            error: () => {
                this.fonctionsService.launch_toast(false, this.translate.instant('ERRORS.FAILED_TO_UPDATE_PONDERATION'));
            }
        });
    };

    if (this.isNew) {
        this.sitesAreaService.createSite(this.getSiteDataFromForm()).subscribe({
            next: (createdSite) => {
                this.onClose(PopupCloseMode.CLOSE);
                this.fonctionsService.launch_toast(true, this.translate.instant('SUCCESS.SITE_SAVED'));
                updatePonderation(createdSite.siteId);
            },
            error: () => {
                this.fonctionsService.launch_toast(false, this.translate.instant('ERRORS.FAILED_TO_SAVE_SITE'));
            }
        });
    } else {
        this.sitesAreaService.updateSite(this.getSiteDataFromForm()).subscribe({
            next: (updatedSite) => {
                this.onClose(PopupCloseMode.CLOSE, updatedSite);
                this.fonctionsService.launch_toast(true, this.translate.instant('SUCCESS.SITE_SAVED'));
                updatePonderation(this.siteId);
            },
            error: () => {
                this.fonctionsService.launch_toast(false, this.translate.instant('ERRORS.FAILED_TO_SAVE_SITE'));
            }
        });
    }
}

  parseEpsgIndex(data: any): EpsgIndex {
    const parsedIndex: EpsgIndex = {};

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const item = data[key];
        parsedIndex[key] = {
          name: item.name || 'Unknown',
          proj4: item.proj4 || null,
          wkt: item.wkt || null,
          bbox: Array.isArray(item.bbox) ? item.bbox : [],
          unit: item.unit || 'Unknown',
          area: item.area || 'Unknown',
          accuracy: typeof item.accuracy === 'number' ? item.accuracy : null
        };
      }
    }

    return parsedIndex;
  }

  protected compareItems(item1: any, item2: any): boolean {
    return item1 && item2 ? item1 === item2 : item1 === item2;
  }
  getSiteDataFromForm(): Site {
    const formValue = this.form.value;
  
    const siteData: Site = {
      regionId: formValue.region,
      coordX: formValue.x,
      coordY: formValue.y,
      nom: formValue.name,
      paysId: formValue.country,
      epsgCoordinates: this.isNew ? formValue.epsg : formValue.epsg?.id,
      epsg: this.isNew ? formValue.epsgSite : formValue.epsgSite?.id,
      isActif: formValue.isActif,
      siteId: this.site?.siteId || 0,
      region: undefined,
      pays: undefined,
      groupByNomRegion: '',
      nbUtilisateurs: 0,
      nbZones: 0,
      profilId: undefined,
      projets: [],
    };
  
    return siteData;
  }
  onPonderationChange(selectedValue: any): void {
    this.selectedPonderationId = typeof selectedValue === 'number' ? selectedValue : selectedValue?.ponderationId ?? null;
    this.cdr.detectChanges();
  }

  private getSelectedStandardCode(): string | null {
    const selectedPonderation = this.ponderations.find((ponderation) => ponderation.standardCode === this.selectedStandardCode);
    return selectedPonderation?.standardCode ?? null;
  }
}